import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Modal,
  Fade,
  Backdrop,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Autocomplete,
  TextField,
  Radio,
  Stack
} from '@material-ui/core';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import MMALoadComponent from './MMALoadComponent';
import { useIsFeatureFlagEnabled } from '../../../utils/FeatureFlags';
import ComponentSystemSelect from './ComponentTargetSystemSelect';
import useVrackApi from '../../../hooks/useVrackApi';

const styles = {
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    p: 4
  },
  modalFooter: {
    p: 1
  },
  componentDetails: {
    marginTop: 1,
    marginBottom: 2
  },
  componentsTable: {
    marginY: 2
  },
  tableReleaseNotes: {
    whiteSpace: 'nowrap',
    maxWidth: '200px',
    width: '100px',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
};
const defaultComponentHelperText = 'Part number of the component to load';

function getComponentVersions(componentVersions) {
  const components = componentVersions.map((v) => v.version);
  return components.reverse();
}

AddNewComponentModal.propTypes = {
  componentsList: PropTypes.object,
  setComponentsList: PropTypes.func,
  systemType: PropTypes.string
};

export default function AddNewComponentModal({ componentsList, setComponentsList, systemType }) {
  const [isOpen, setIsOpen] = useState(false);
  const [sourceValue, setSourceValue] = useState('scmdb');
  const [typeValue, setTypeValue] = useState('software');
  const [partNumberValue, setPartNumberValue] = useState('');
  const [versionValue, setVersionValue] = useState('');
  const [componentVersions, setComponentVersions] = useState([]);
  const [filenameValue, setFilenameValue] = useState('');
  const [uploaded, setUploaded] = useState([]);
  const [validComponentNumber, setValidComponentNumber] = useState(false);
  const [mmaAirline, setMMAAirline] = useState(null);
  const [mmaProfile, setMMAProfile] = useState(null);
  const [mmaCycle, setMMACycle] = useState('');
  const [mmaExportVersion, setMMAExportVersion] = useState(null);
  const isMMAEnabled = useIsFeatureFlagEnabled('media_metadata');
  const [searchParams, setSearchParams] = useSearchParams();
  const [componentTargetSystem, setComponentTargetSystem] = useState('ife');
  const [disableAddComponent, setDisableAddComponent] = useState(true);
  const [componentHelperText, setComponentHelperText] = useState(defaultComponentHelperText);
  const { fetchData: fetchComponentVersionFromVrack } = useVrackApi(
    'get',
    `/scmdb/component/:partnumber/versions`
  );
  const { fetchData: fetchUploadedComponentsFromVrack } = useVrackApi(
    'get',
    `/identities/:identity/loadables`
  );

  const identity = useSelector((state) => state.identity.value);
  const invalidComponentHelperText = 'Invalid Component provided';
  const componentPNExistsHelperText = 'Component partnumber already exists in upload list';

  const openHandler = () => {
    searchParams.append('new_component', true);
    setSearchParams(searchParams);
    setIsOpen(true);
  };
  const closeHandler = () => {
    searchParams.delete('new_component');
    setSearchParams(searchParams);
    setIsOpen(false);
    setPartNumberValue('');
    setVersionValue('');
  };

  const addComponent = (components) => {
    // change the payload and type depending on the type of source value they chose
    let componentPayload = {};
    let componentType = typeValue;
    const getComponentSystem = systemType === 'standalone' ? 'gcs' : componentTargetSystem;

    switch (sourceValue) {
      case 'aws':
        if (sourceValue === 'aws' && filenameValue === '') {
          console.error('filename cannot be empty');
          return;
        }
        componentPayload = {
          source: sourceValue,
          filename: filenameValue,
          system: getComponentSystem
        };
        break;
      case 'scmdb':
        if (partNumberValue === '' || versionValue === '') {
          console.error('part number or version cannot be empty');
          return;
        }
        componentPayload = {
          partnumber: partNumberValue,
          version: versionValue,
          source: sourceValue,
          system: getComponentSystem
        };
        break;
      case 'mma':
        if (!mmaAirline || !mmaProfile || mmaCycle === '' || !mmaExportVersion) {
          console.error(
            'mma fields cannot be empty',
            mmaAirline,
            mmaProfile,
            mmaCycle,
            mmaExportVersion
          );
          return;
        }
        componentPayload = {
          source: 'mma',
          version: mmaExportVersion.export_version,
          mediainfo: {
            airline: mmaAirline.icao?.toLowerCase(),
            profile: mmaProfile.name,
            cycle: mmaCycle,
            version: mmaExportVersion.export_version,
            mediatype: 'preview_kit'
          },
          system: getComponentSystem
        };
        componentType = 'media';
        break;
      default:
        break;
    }

    // adds a component to the current list
    const newComponents = { ...components };
    newComponents[componentType].push(componentPayload);
    setComponentsList(newComponents);
    // clears input values
    setPartNumberValue('');
    setVersionValue('');
    setFilenameValue('');
    closeHandler();
  };

  const handlePartNumberChange = (event, newValue) => {
    setVersionValue('');
    if (sourceValue === 'aws') {
      setPartNumberValue(newValue);
      setComponentVersions([]);
    } else {
      setPartNumberValue(event.target.value);
      // query available versions of the component
      fetchComponentVersions(event.target.value);
    }
  };

  const handleVersionChange = (event, newValue) => {
    setVersionValue(newValue);
  };

  const handleFilenameChange = (event, newValue) => {
    setFilenameValue(newValue);
  };

  const handleTypeChange = (event) => {
    setTypeValue(event.target.value);
  };

  const handleSourceChange = (event) => {
    setPartNumberValue('');
    setVersionValue('');
    setFilenameValue('');

    if (event.target.value === 'aws') {
      fetchUploadedComponents();
    }
    setSourceValue(event.target.value);
  };

  const getUploadedOptions = () => {
    // get all the filenames that were added to the componentsList already
    const existingFiles = [];
    Object.keys(componentsList).forEach((key) => {
      const components = componentsList[key];
      components.forEach((component) => {
        if (component.filename) {
          existingFiles.push(component.filename);
        }
      });
    });
    // return a list of filenames excluding the ones added already
    return uploaded.filter((filename) => !existingFiles.includes(filename));
  };

  const fetchUploadedComponents = async () => {
    await fetchUploadedComponentsFromVrack({
      url: `/identities/${identity}/loadables`
    })
      .then((resp) => {
        const data = resp.components;
        setUploaded(data.map((c) => c.filename));
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const fetchComponentVersions = async (partnumber) => {
    try {
      const resp = await fetchComponentVersionFromVrack({
        url: `/scmdb/component/${partnumber}/versions`
      });
      if (resp) {
        setComponentVersions(resp);
        setValidComponentNumber(true);
      } else {
        setComponentVersions([]);
      }
    } catch (err) {
      console.error(err);
      setComponentVersions([]);
    }
  };

  // checks if the current component partnumber being added already exists on the current component list
  const checkComponentExists = () => {
    const componentFound = (comp) => comp.partnumber === partNumberValue;
    const componentIndex = componentsList[typeValue].findIndex(componentFound);
    // index will be greater than -1 if component is found
    return componentIndex > -1;
  };

  // returns the Component Details section that dictates extra information to be selected for the component
  const getComponentDetails = () => {
    switch (sourceValue) {
      case 'aws':
        return (
          <Autocomplete
            id="filename"
            size="small"
            onInputChange={handleFilenameChange}
            options={getUploadedOptions()}
            value={filenameValue}
            renderInput={(params) => (
              <TextField
                {...params}
                helperText="Filename of the component to load"
                label="Filename"
              />
            )}
            sx={styles.componentDetails}
          />
        );
      case 'mma':
        return (
          <MMALoadComponent
            mmaAirline={mmaAirline}
            setMMAAirline={setMMAAirline}
            mmaProfile={mmaProfile}
            setMMAProfile={setMMAProfile}
            mmaCycle={mmaCycle}
            setMMACycle={setMMACycle}
            mmaExportVersion={mmaExportVersion}
            setMMAExportVersion={setMMAExportVersion}
          />
        );
      default:
        return (
          <Stack spacing={2} sx={styles.componentDetails}>
            <TextField
              error={!validComponentNumber}
              size="small"
              required
              inputProps={{ 'data-testid': 'component-partnumber' }}
              label="Part Number"
              value={partNumberValue}
              sx={{ width: '100%' }}
              helperText={validComponentNumber ? defaultComponentHelperText : componentHelperText}
              onChange={handlePartNumberChange}
            />
            <Autocomplete
              data-testid="component-version-autocomplete"
              size="small"
              options={getComponentVersions(componentVersions)}
              onInputChange={handleVersionChange}
              value={versionValue}
              inputprops={{ 'data-testid': 'component-version' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  helperText="Version of the component"
                  label="Select a Component Version"
                />
              )}
              sx={styles.componentDetails}
            />
          </Stack>
        );
    }
  };
  const validatePartnumber = () => {
    if (componentVersions.length === 0 && partNumberValue.length > 0) {
      setValidComponentNumber(false);
      setComponentHelperText(invalidComponentHelperText);
    } else {
      setValidComponentNumber(true);
      setComponentHelperText(defaultComponentHelperText);
    }
  };

  useEffect(() => {
    validatePartnumber();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentVersions]);

  useEffect(() => {
    if (checkComponentExists()) {
      setValidComponentNumber(true);
      setComponentHelperText(componentPNExistsHelperText);
    } else {
      setValidComponentNumber(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partNumberValue]);

  useEffect(() => {
    const validVersion = componentVersions.some(
      (componentVersion) => componentVersion.version === versionValue
    );

    if (versionValue !== '' && validVersion) {
      setDisableAddComponent(false);
    } else {
      setDisableAddComponent(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionValue]);

  useEffect(() => {
    // change the default type value depending on new source value
    if (sourceValue === 'mma') {
      setTypeValue('metadata');
    } else {
      setTypeValue('software');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceValue]);

  return (
    <>
      <Button data-testid="new-component-button" variant="outlined" onClick={openHandler}>
        Add New Component
      </Button>

      <Modal
        open={isOpen}
        onClose={closeHandler}
        sx={{ outline: 'none' }}
        aria-labelledby="add-new-component-modal"
        aria-describedby="modal to add a new component"
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Fade in={isOpen}>
          <Grid
            data-testid="add-new-component-modal-content"
            direction="row"
            sx={styles.modal}
            container
          >
            <Grid item xs={12} md={1} lg={3} onClick={closeHandler} />
            <Grid item xs={12} md={10} lg={6}>
              <Card>
                <CardHeader sx={{ padding: 2 }} title="Add New Component" />
                <Divider />
                <CardContent sx={{ paddingTop: 1 }}>
                  <Stack spacing={2}>
                    <FormControl>
                      <FormLabel>Source</FormLabel>
                      <RadioGroup row value={sourceValue} onChange={handleSourceChange}>
                        <FormControlLabel
                          value="scmdb"
                          control={<Radio size="small" />}
                          label="SCMDB"
                        />
                        <FormControlLabel
                          value="aws"
                          control={<Radio size="small" />}
                          label="Team Loadable"
                        />
                        {isMMAEnabled && (
                          <FormControlLabel
                            value="mma"
                            control={<Radio size="small" />}
                            label="MMA"
                          />
                        )}
                      </RadioGroup>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Type</FormLabel>
                      <RadioGroup row value={typeValue} onChange={handleTypeChange}>
                        {sourceValue !== 'mma' ? (
                          <>
                            <FormControlLabel
                              value="software"
                              control={<Radio size="small" />}
                              label="Software"
                            />
                            <FormControlLabel
                              value="content"
                              control={<Radio size="small" />}
                              label="Content"
                            />
                            <FormControlLabel
                              value="onemedia"
                              control={<Radio size="small" />}
                              label="One Media"
                            />
                          </>
                        ) : (
                          <FormControlLabel
                            value="metadata"
                            control={<Radio size="small" />}
                            label="Media Metadata"
                          />
                        )}
                      </RadioGroup>
                    </FormControl>
                    {systemType === 'decoupled' && (
                      <Grid item xs={12}>
                        <ComponentSystemSelect
                          data-testid="component-system"
                          setComponentTargetSystem={setComponentTargetSystem}
                          componentTargetSystem={componentTargetSystem}
                        />
                      </Grid>
                    )}

                    <Grid container>
                      <Grid item xs={12}>
                        <FormLabel>Component Details</FormLabel>
                        {getComponentDetails()}
                      </Grid>
                    </Grid>
                  </Stack>
                </CardContent>
                <Divider />
                <Stack
                  justifyContent="flex-end"
                  direction="row"
                  sx={styles.modalFooter}
                  spacing={1}
                >
                  <Button
                    sx={{ marginLeft: 1 }}
                    color="secondary"
                    variant="outlined"
                    onClick={closeHandler}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={
                      sourceValue !== 'aws' &&
                      sourceValue !== 'mma' &&
                      (disableAddComponent || !validComponentNumber)
                    }
                    variant="contained"
                    color="success"
                    onClick={() => {
                      addComponent(componentsList);
                    }}
                  >
                    Add Component
                  </Button>
                </Stack>
              </Card>
            </Grid>
            <Grid item xs={12} med={1} lg={3} onClick={closeHandler} />
          </Grid>
        </Fade>
      </Modal>
    </>
  );
}
